import React from 'react'
import { StandardColumn } from '../../../../components/StandardPage'
import { stripHtml } from '../../../../utils/stripHtml'
import { Select } from './style'

export const productionFashionOrdersScreenKey = 'production_fashion_order'

export interface UserProfileData {
  id: number
  name: string
}

export interface UserData {
  id: number
  name: string
  email?: string
  profile?: UserProfileData
  profileId?: number | null
}

export interface FashionProductionItemsData {
  id: number
  serviceId: number
  fashionOrderId: number
  quantityForThisProduction: number
  fashionOrder?: {
    id: number
    Company_Order_clientIdToCompany?: { companyName: string }
  }
}

export interface FashionProductionOrderData {
  id: number
  set: string
  status: number
  dressmakerId: number | null
  cutterId: number | null
  productId?: number | null
  collectionColorId?: number | null
  codeOfOP?: string
  Product?: { id: number; name: string } | null
  CollectionColor?: { id: number; name: string } | null
  User_Services_cutterIdToUser?: UserData | null
  User_Services_dressmakerIdToUser?: UserData | null
  FashionProductionItems?: FashionProductionItemsData[]
  OrderItem?: {
    id: number
    ploomesProductName?: string
    Resource?: { name: string } | null
    Material?: { name: string } | null
  } | null
  totalItems?: number
  materialsAvailable?: boolean | null
}

export const getFashionProductionStatusLabel = (status: number): string => {
  const statusMap: { [key: number]: string } = {
    0: 'Pendente',
    1: 'Iniciado',
    2: 'Concluído',
  }
  return statusMap[status] || String(status)
}

const getProductName = (order: FashionProductionOrderData): string =>
  order.Product?.name ||
  stripHtml(order.OrderItem?.ploomesProductName) ||
  order.OrderItem?.Material?.name ||
  order.OrderItem?.Resource?.name ||
  ''

const getPedidoLabel = (order: FashionProductionOrderData): string =>
  order.FashionProductionItems && order.FashionProductionItems.length > 0
    ? order.FashionProductionItems.map((item) => `PED-${item.fashionOrderId}`).join(', ')
    : ''

interface ProductionFashionColumnsOptions {
  cuttersList: UserData[]
  dressmakersList: UserData[]
  onCutterChange: (e: React.ChangeEvent<HTMLSelectElement>, orderId: number) => void
  onDressmakerChange: (e: React.ChangeEvent<HTMLSelectElement>, orderId: number) => void
}

export const buildProductionFashionColumns = ({
  cuttersList,
  dressmakersList,
  onCutterChange,
  onDressmakerChange,
}: ProductionFashionColumnsOptions): StandardColumn<FashionProductionOrderData>[] => [
  {
    id: 'codeOfOP',
    label: 'OP',
    accessor: (order) => order.id,
    cell: (order) => `OP-${order.id}`,
  },
  {
    id: 'fashionOrderId',
    label: 'Pedido',
    accessor: (order) => getPedidoLabel(order),
    cell: (order) => getPedidoLabel(order) || 'N/A',
  },
  {
    id: 'product',
    label: 'Produto',
    accessor: (order) => getProductName(order),
    cell: (order) => getProductName(order) || 'N/A',
  },
  {
    id: 'color',
    label: 'Cor',
    accessor: (order) => order.CollectionColor?.name || '',
    cell: (order) => order.CollectionColor?.name || 'N/A',
  },
  {
    id: 'totalItems',
    label: 'Qtd. de itens',
    accessor: (order) => order.totalItems ?? 0,
    cell: (order) => order.totalItems,
  },
  {
    id: 'status',
    label: 'Status',
    accessor: (order) => getFashionProductionStatusLabel(order.status),
    cell: (order) => getFashionProductionStatusLabel(order.status),
  },
  {
    id: 'cutter',
    label: 'Cortador',
    sortable: false,
    accessor: (order) => order.User_Services_cutterIdToUser?.name || '',
    cell: (order) => (
      <Select
        className="custom-select"
        value={order.cutterId || 0}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onCutterChange(e, order.id)}
      >
        {cuttersList.map((cutter) => (
          <option key={cutter.id} value={cutter.id}>
            {cutter.name}
          </option>
        ))}
      </Select>
    ),
  },
  {
    id: 'dressmaker',
    label: 'Costureira',
    sortable: false,
    accessor: (order) => order.User_Services_dressmakerIdToUser?.name || '',
    cell: (order) => (
      <Select
        className="custom-select"
        value={order.dressmakerId || 0}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onDressmakerChange(e, order.id)}
      >
        {dressmakersList.map((dressmaker) => (
          <option key={dressmaker.id} value={dressmaker.id}>
            {dressmaker.name}
          </option>
        ))}
      </Select>
    ),
  },
  {
    id: 'materialsAvailable',
    label: 'Materiais',
    sortable: false,
    accessor: (order) =>
      order.materialsAvailable === null || order.materialsAvailable === undefined
        ? 'Verificando...'
        : order.materialsAvailable
        ? 'Materiais suficientes'
        : 'Materiais insuficientes',
    cell: (order) => (
      <span
        title={
          order.materialsAvailable === null || order.materialsAvailable === undefined
            ? 'Verificando...'
            : order.materialsAvailable
            ? 'Materiais suficientes'
            : 'Materiais insuficientes'
        }
        style={{
          color:
            order.materialsAvailable === null || order.materialsAvailable === undefined
              ? '#9ca3af'
              : order.materialsAvailable
              ? '#adf0c7'
              : '#ffc6c6',
          fontSize: '1.6rem',
          lineHeight: 1,
          cursor: 'default',
        }}
      >
        ⬤
      </span>
    ),
  },
]
